import { useCallback, useEffect, useRef, useState } from 'react';
import API_CONFIG from '../../config/api';
import { ExportCancelled, registerExportOtpPrompt, saveExportToken } from '../../utils/exportOtp';

// The OTP step in front of every report export. Mounted once in the
// dashboard layout; ensureExportToken() (utils/exportOtp.js) opens it from
// anywhere and gets a promise back. Same 4-digit code and the same SMS/email
// senders as the sign-in OTP.
const OTP_LENGTH = 4;
const RESEND_SECONDS = 30;

const sessionUserId = () => {
    try {
        const session = JSON.parse(localStorage.getItem('adminSession') || 'null');
        return session?.userId || session?.id || null;
    } catch (e) {
        return null;
    }
};

const emptyDigits = () => Array(OTP_LENGTH).fill('');

export default function ExportOtpProvider() {
    const [request, setRequest] = useState(null);
    const [status, setStatus] = useState('idle'); // sending | ready | verifying | error
    const [sentTo, setSentTo] = useState({ phone: '', email: '' });
    const [digits, setDigits] = useState(emptyDigits);
    const [error, setError] = useState('');
    const [resendIn, setResendIn] = useState(0);
    const requestRef = useRef(null);
    const inputs = useRef([]);

    const close = useCallback(() => {
        requestRef.current = null;
        setRequest(null);
        setStatus('idle');
        setError('');
        setDigits(emptyDigits());
    }, []);

    const finish = useCallback((token) => {
        const current = requestRef.current;
        close();
        current?.resolve(token);
    }, [close]);

    const cancel = useCallback(() => {
        const current = requestRef.current;
        close();
        current?.reject(new ExportCancelled());
    }, [close]);

    const sendCode = useCallback(async (purpose) => {
        setStatus('sending');
        setError('');
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/export-otp/request`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-user-id': sessionUserId() },
                body: JSON.stringify({ purpose })
            });
            const data = await res.json().catch(() => ({}));
            if (data.exempt) {
                saveExportToken({ exempt: true });
                finish('');
                return;
            }
            if (!res.ok || !data.success) throw new Error(data.message || 'Could not send the OTP');
            setSentTo({ phone: data.maskedPhone || '', email: data.maskedEmail || '' });
            setDigits(emptyDigits());
            setStatus('ready');
            setResendIn(RESEND_SECONDS);
            setTimeout(() => inputs.current[0]?.focus(), 50);
        } catch (e) {
            setStatus('error');
            setError(e.message || 'Could not send the OTP');
        }
    }, [finish]);

    useEffect(() => registerExportOtpPrompt((purpose) => new Promise((resolve, reject) => {
        // A second export while the prompt is open shares the same code.
        if (requestRef.current) {
            const previous = requestRef.current;
            requestRef.current = {
                purpose: previous.purpose,
                resolve: (token) => { previous.resolve(token); resolve(token); },
                reject: (reason) => { previous.reject(reason); reject(reason); }
            };
            return;
        }
        const next = { purpose: String(purpose || ''), resolve, reject };
        requestRef.current = next;
        setRequest(next);
        sendCode(next.purpose);
    })), [sendCode]);

    useEffect(() => {
        if (resendIn <= 0) return undefined;
        const timer = setTimeout(() => setResendIn((seconds) => seconds - 1), 1000);
        return () => clearTimeout(timer);
    }, [resendIn]);

    useEffect(() => {
        if (!request) return undefined;
        const onKey = (event) => { if (event.key === 'Escape') cancel(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [request, cancel]);

    const verify = useCallback(async (code) => {
        const otp = (code || digits.join('')).trim();
        if (otp.length !== OTP_LENGTH) {
            setError(`Enter the ${OTP_LENGTH}-digit code`);
            return;
        }
        setStatus('verifying');
        setError('');
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}/api/admin/export-otp/verify`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'x-user-id': sessionUserId() },
                body: JSON.stringify({ otp })
            });
            const data = await res.json().catch(() => ({}));
            if (data.exempt) {
                saveExportToken({ exempt: true });
                finish('');
                return;
            }
            if (!res.ok || !data.success) throw new Error(data.message || 'Invalid OTP');
            saveExportToken({ token: data.token, expiresAt: data.expiresAt });
            finish(data.token);
        } catch (e) {
            setStatus('ready');
            setError(e.message || 'Invalid OTP');
            setDigits(emptyDigits());
            setTimeout(() => inputs.current[0]?.focus(), 50);
        }
    }, [digits, finish]);

    const setDigit = (index, value) => {
        const digit = value.replace(/\D/g, '').slice(-1);
        const next = [...digits];
        next[index] = digit;
        setDigits(next);
        setError('');
        if (digit && index < OTP_LENGTH - 1) inputs.current[index + 1]?.focus();
        if (digit && next.every(Boolean)) verify(next.join(''));
    };

    const onKeyDown = (index, event) => {
        if (event.key === 'Backspace' && !digits[index] && index > 0) inputs.current[index - 1]?.focus();
        if (event.key === 'Enter') verify();
    };

    const onPaste = (event) => {
        const pasted = (event.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, OTP_LENGTH);
        if (!pasted) return;
        event.preventDefault();
        const next = emptyDigits();
        pasted.split('').forEach((d, i) => { next[i] = d; });
        setDigits(next);
        inputs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
        if (pasted.length === OTP_LENGTH) verify(pasted);
    };

    if (!request) return null;

    const purposeText = request.purpose ? `To export ${request.purpose}, ` : 'To export, ';
    const channels = [sentTo.phone && `SMS to ${sentTo.phone}`, sentTo.email && `email to ${sentTo.email}`].filter(Boolean);

    return (
        <div className="admin-modal-overlay" onClick={cancel}>
            <div
                className="admin-modal-content export-otp-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="export-otp-title"
                onClick={(event) => event.stopPropagation()}
            >
                <div className="export-otp-icon" aria-hidden="true">🔐</div>
                <h3 id="export-otp-title">Verify before exporting</h3>

                {status === 'sending' && (
                    <p className="export-otp-copy">{purposeText}a one-time code is being sent to you…</p>
                )}

                {status === 'error' && (
                    <>
                        <p className="export-otp-error" role="alert">{error}</p>
                        <div className="admin-modal-actions">
                            <button type="button" className="admin-modal-btn cancel" onClick={cancel}>Cancel</button>
                            <button type="button" className="admin-modal-btn confirm" onClick={() => sendCode(request.purpose)}>Try again</button>
                        </div>
                    </>
                )}

                {(status === 'ready' || status === 'verifying') && (
                    <>
                        <p className="export-otp-copy">
                            {purposeText}enter the code sent by {channels.join(' and ') || 'SMS / email'}.
                        </p>
                        <div className="export-otp-inputs" onPaste={onPaste}>
                            {digits.map((digit, index) => (
                                <input
                                    key={index}
                                    ref={(el) => { inputs.current[index] = el; }}
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    maxLength={1}
                                    value={digit}
                                    disabled={status === 'verifying'}
                                    aria-label={`Digit ${index + 1}`}
                                    onChange={(event) => setDigit(index, event.target.value)}
                                    onKeyDown={(event) => onKeyDown(index, event)}
                                />
                            ))}
                        </div>
                        {error && <p className="export-otp-error" role="alert">{error}</p>}
                        <div className="admin-modal-actions">
                            <button type="button" className="admin-modal-btn cancel" onClick={cancel} disabled={status === 'verifying'}>Cancel</button>
                            <button type="button" className="admin-modal-btn confirm" onClick={() => verify()} disabled={status === 'verifying'}>
                                {status === 'verifying' ? 'Verifying…' : 'Verify & export'}
                            </button>
                        </div>
                        <button
                            type="button"
                            className="export-otp-resend"
                            disabled={resendIn > 0 || status === 'verifying'}
                            onClick={() => sendCode(request.purpose)}
                        >
                            {resendIn > 0 ? `Resend code in ${resendIn}s` : 'Resend code'}
                        </button>
                        <p className="export-otp-note">Every export is recorded against your account.</p>
                    </>
                )}
            </div>
        </div>
    );
}
